import React from 'react'
import {Button, Card} from 'react-daisyui'
import {
  useGetCMSPropertiesQuery,
  useGetPMSPropertiesQuery
} from '../../services/guestPortalApi'
import {formatDateRange} from '../../services/utils'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faUser, faBed, faHashtag} from '@fortawesome/free-solid-svg-icons'
import {useNavigate} from 'react-router-dom'
import StaysEmptyState from '../../assets/checkin-room.png'
import {trackBookingManagement} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'

const StayCard = ({
  stay,
  viewReservation,
  onManageStay
}: {
  stay: any
  viewReservation?: boolean
  onManageStay?: Function
}) => {
  const propertiesQuery = useGetCMSPropertiesQuery({})
  const pmsPropertiesQuery = useGetPMSPropertiesQuery({})

  const navigate = useNavigate()

  return (
    <div>
      {propertiesQuery.data && (
        <Card
          className="md:h-[275px] md:card-side bg-white shadow-md"
          // aria-label={`Stay card for ${
          //   propertiesQuery.data[stay.propertyId].name
          // }`}
        >
          {propertiesQuery.data[stay.propertyId] ? (
            <Card.Image
              src={
                (propertiesQuery.data[stay.propertyId].images &&
                  propertiesQuery.data[stay.propertyId].images[0].image) ||
                StaysEmptyState
              }
              alt={
                (propertiesQuery.data[stay.propertyId].images &&
                  propertiesQuery.data[stay.propertyId].images[0]?.altText) ||
                'Stay image'
              }
              className="h-full object-cover"
            />
          ) : (
            <Card.Image
              src={StaysEmptyState}
              alt="Stay image"
              className="h-full object-cover object-left"
            />
          )}
          <Card.Body className="p-4 pt-6 min-w-[250px] relative min-h-[250px]">
            {propertiesQuery.data[stay.propertyId] ? (
              <div>
                <p
                  className="text-xs uppercase font-semibold btn-cabana pb-1"
                  color="secondary"
                  aria-label={`Location: ${
                    propertiesQuery.data[stay.propertyId].location ||
                    'Unknown location'
                  }`}
                >
                  {propertiesQuery.data[stay.propertyId].location || ''}
                </p>
                <p
                  className="text-xl pb-1 line-clamp-2"
                  aria-label={`Property name: ${
                    propertiesQuery.data[stay.propertyId].name
                  }`}
                >
                  {propertiesQuery.data[stay.propertyId].name}
                </p>
              </div>
            ) : (
              <div>
                <p
                  className="text-xs uppercase font-semibold btn-cabana pb-1"
                  color="secondary"
                  aria-label={`Location: ${'Unknown location'}`}
                >
                  {pmsPropertiesQuery.data &&
                  pmsPropertiesQuery.data[stay.propertyId]
                    ? pmsPropertiesQuery.data[stay.propertyId].location?.city
                    : ''}
                </p>
                <p
                  className="text-xl pb-1 line-clamp-2"
                  aria-label={`Property name: ${stay.propertyId}`}
                >
                  {pmsPropertiesQuery.data &&
                  pmsPropertiesQuery.data[stay.propertyId]
                    ? pmsPropertiesQuery.data[stay.propertyId].name
                    : stay.propertyTitle}
                </p>
              </div>
            )}
            <div
              className="container mx-auto text-sm font-normal"
              aria-label="Stay details"
            >
              <p
                className="pb-1"
                aria-label={`Dates: ${
                  (formatDateRange(stay.checkinDate, stay.checkoutDate), 'to')
                }`}
              >
                {formatDateRange(stay.checkinDate, stay.checkoutDate)}
              </p>
              <ul>
                <li
                  className="pb-1 whitespace-nowrap text-ellipsis overflow-hidden"
                  aria-label={`Guests: ${stay.adults} guest${
                    stay.adults > 1 ? 's' : ''
                  }`}
                >
                  <p>
                    <span className="pr-3">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-xs text-primary"
                      />
                    </span>
                    {stay.adults} guest{stay.adults > 1 ? 's' : ''}
                  </p>
                </li>
                <li
                  className="pb-1 whitespace-nowrap text-ellipsis overflow-hidden"
                  aria-label={`Unit type: ${stay.unitType}`}
                >
                  <span className="pr-2">
                    <FontAwesomeIcon
                      icon={faBed}
                      className="text-xs text-primary"
                    />
                  </span>
                  {stay.unitType}
                </li>
                <li
                  className="pb-1 whitespace-nowrap text-ellipsis overflow-hidden"
                  aria-label={`Reservation ID: ${stay.reservationId}`}
                >
                  <span className="pr-3">
                    <FontAwesomeIcon
                      icon={faHashtag}
                      className="text-xs text-primary"
                    />
                  </span>
                  {stay.reservationId}
                </li>
              </ul>
            </div>

            {stay.cancelDate && (
              <div
                className="text-xs text-gray-500 absolute bottom-6 left-4"
                aria-label={`Canceled on ${new Date(
                  stay.cancelDate
                ).toLocaleDateString('en-US', {
                  timeZone: 'UTC'
                })}`}
              >
                Canceled on{' '}
                {new Date(stay.cancelDate).toLocaleDateString('en-US', {
                  timeZone: 'UTC'
                })}
              </div>
            )}

            {stay.status === 'NoShow' && (
              <div
                className="text-xs text-gray-500 pt-8"
                aria-label={`No Show on ${new Date(
                  stay.checkinDate
                ).toLocaleDateString('en-US', {
                  timeZone: 'UTC'
                })}`}
              >
                No Show on{' '}
                {new Date(stay.checkinDate).toLocaleDateString('en-US', {
                  timeZone: 'UTC'
                })}
              </div>
            )}
            {viewReservation ? (
              <Card.Actions className="justify-end pt-5">
                <div className="container flex justify-end pt-25">
                  <Button
                    className="btn-sm text-xs lowercase font-thin md:absolute md:bottom-4 md:right-4"
                    color="secondary"
                    onClick={evt => {
                      // Track view reservation click
                      const token = getUserToken()
                      const user = token ? getUser(token) : null
                      trackClick(
                        'view-reservation-button',
                        `/member/stays/${stay.reservationId}`,
                        user?.email
                      )
                      trackBookingManagement(
                        'view_booking',
                        stay.reservationId,
                        undefined,
                        user?.email
                      )

                      navigate(`/member/stays/${stay.reservationId}`)
                      if (onManageStay) {
                        onManageStay(stay)
                      }
                    }}
                    // aria-label={`View reservation for ${
                    //   propertiesQuery.data[stay.propertyId].name
                    // }`}
                  >
                    View Reservation
                  </Button>
                </div>
              </Card.Actions>
            ) : (
              ''
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default StayCard
